<template>
    <BaseCard padding="none" class="flex flex-col">
        <div class="flex flex-col gap-3 p-5 md:flex-row md:flex-wrap md:items-center md:gap-4">
            <div class="min-w-0 grow md:basis-64 flex flex-col gap-1" data-testid="campaign-summary">
                <div class="flex items-center gap-2 flex-wrap">
                    <span class="body-semibold text-ink truncate">{{ campaign.name }}</span>
                    <StatusChip :state="statusState">{{ statusLabel }}</StatusChip>
                </div>
                <div class="body-small text-ink-muted md:truncate">{{ metaLine }}</div>
                <div v-if="campaign.has_report && campaign.report_generated_at" class="details text-ink-muted md:truncate">
                    {{ t("reflectionsAdmin.campaign.reportGenerated", { date: formatDate(campaign.report_generated_at, "short") }) }}
                    <span v-if="campaign.new_submissions_since_report > 0" class="text-ink body-small-bold">
                        {{ t("reflectionsAdmin.campaign.newSinceReport", { count: campaign.new_submissions_since_report }) }}
                    </span>
                </div>
                <div v-else-if="campaign.closes_at" class="details text-ink-muted md:truncate">
                    {{ t("reflectionsAdmin.campaign.closes", { date: formatDate(campaign.closes_at, "short") }) }}
                </div>
                <div v-else class="details text-ink-muted md:truncate">{{ t("reflectionsAdmin.campaign.noReportYet") }}</div>
            </div>

            <div v-if="hasParticipants" class="progress-block" data-testid="campaign-progress">
                <div class="flex justify-between details text-ink-muted">
                    <span>
                        <strong class="body-small-semibold text-ink">{{ t("reflectionsAdmin.campaign.percent", { pct: submittedPct }) }}</strong>
                        {{ t("reflectionsAdmin.campaign.submittedSuffix") }}
                    </span>
                    <span>{{ t("reflectionsAdmin.campaign.ofTotal", { count: campaign.submitted, total: campaign.participants }) }}</span>
                </div>
                <div class="progress-bar">
                    <span class="progress-fill fill-submitted" :style="{ width: widthOf(campaign.submitted) }"></span>
                    <span class="progress-fill fill-in-progress" :style="{ width: widthOf(campaign.in_progress) }"></span>
                </div>
                <div v-if="!campaign.is_self_assessment_only" class="details text-ink-muted">{{ raterLine }}</div>
                <div class="details text-ink-muted flex items-center gap-2">
                    <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
                    {{ t("reflectionsAdmin.campaign.reportsReady", { ready: campaign.reports_ready, total: campaign.participants, viewed: campaign.reports_viewed }) }}
                </div>
            </div>

            <div class="flex items-center gap-2 flex-wrap md:shrink-0 md:ml-auto" data-testid="campaign-actions">
                <BaseButton
                    v-if="hasParticipants"
                    type="button"
                    theme="neutral"
                    size="sm"
                    class="inline-flex items-center gap-2"
                    :class="{ 'bg-toggle-option-selected': expanded }"
                    :aria-expanded="expanded"
                    :aria-controls="panelId"
                    @click="emit('toggle', campaign.id)">
                    <i class="bi bi-people" aria-hidden="true"></i>
                    {{ t("reflectionsAdmin.campaign.participants") }}
                    <i :class="['bi', expanded ? 'bi-chevron-up' : 'bi-chevron-down', 'text-xs']" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton
                    v-if="campaign.state === 'active'"
                    type="button"
                    theme="neutral"
                    size="sm"
                    class="inline-flex items-center gap-2"
                    :disabled="busy"
                    @click="emit('close', campaign)">
                    <i class="bi bi-x" aria-hidden="true"></i>
                    {{ t("reflectionsAdmin.campaign.close") }}
                </BaseButton>
                <BaseButton type="button" theme="primary" size="sm" @click="emit('view', campaign.id)">{{ t("reflectionsAdmin.campaign.view") }}</BaseButton>
            </div>
        </div>

        <div v-if="expanded && hasParticipants" :id="panelId" class="border-t border-border">
            <AdminCampaignParticipantsPanel
                :campaign-id="campaign.id"
                :campaign-state="campaign.state"
                :participant-count="campaign.participants"
                :raters-invited="campaign.raters_invited"
                :hide-rater-status="hideRaterStatus"
                :is-self-assessment-only="campaign.is_self_assessment_only ?? false" />
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import type { AdminCampaignSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import AdminCampaignParticipantsPanel from "~vue/pages/reflections/AdminCampaignParticipantsPanel.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    campaign: AdminCampaignSummary;
    busy: boolean;
    expanded?: boolean;
    hideRaterStatus?: boolean;
}

const { campaign, busy, expanded = false, hideRaterStatus = false } = defineProps<Props>();
const emit = defineEmits<{
    (event: "close", campaign: AdminCampaignSummary): void;
    (event: "view", campaignId: string): void;
    (event: "toggle", campaignId: string): void;
}>();
const { t } = useI18n();

const panelId = computed(() => `campaign-panel-${campaign.id}`);
const hasParticipants = computed(() => campaign.participants > 0);
const submittedPct = computed(() => (campaign.participants ? Math.round((campaign.submitted / campaign.participants) * 100) : 0));

const metaLine = computed(() => {
    // Responses counts every submission, raters included, as the card always has; the bar tracks participants.
    const counts = { template: campaign.template.name, participants: campaign.participants, responses: campaign.submitted_response_count };
    return campaign.is_self_assessment_only ? t("reflectionsAdmin.campaign.metaSelfOnly", counts) : t("reflectionsAdmin.campaign.meta", counts);
});

const raterLine = computed(() => {
    if (hideRaterStatus || campaign.raters_responded === null || campaign.raters_responded === undefined) {
        return t("reflectionsAdmin.campaign.ratersInvited", { count: campaign.raters_invited });
    }
    return t("reflectionsAdmin.campaign.ratersResponded", { responded: campaign.raters_responded, invited: campaign.raters_invited });
});

const statusLabel = computed(() => {
    switch (campaign.state) {
        case "active":
            return t("reflectionsAdmin.campaign.state.active");
        case "draft":
            return t("reflectionsAdmin.campaign.state.draft");
        default:
            return t("reflectionsAdmin.campaign.state.closed");
    }
});

const statusState = computed(() => {
    switch (campaign.state) {
        case "active":
            return "active";
        case "draft":
            return "pending";
        default:
            return "neutral";
    }
});

function widthOf(count: number): string {
    // Clamped like the completion glance: a cycle can outlive its subject's
    // eligibility, so a segment can exceed the whole.
    return campaign.participants ? `${Math.min(100, (count / campaign.participants) * 100)}%` : "0%";
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .progress-block {
        @apply flex flex-col gap-2 w-full md:w-64 md:shrink-0;
    }

    .progress-bar {
        @apply flex w-full h-2 rounded-full overflow-hidden;
        background: var(--color-progress-track);
    }

    .progress-fill {
        @apply h-full transition-[width] duration-300;
    }

    .fill-submitted {
        background: var(--color-accent);
    }

    .fill-in-progress {
        background: var(--color-status-warning);
    }
}
</style>
